import React from 'react';
import {
  Document,
  Page,
  View,
  Text,
  StyleSheet,
  Font,
  Image,
} from '@react-pdf/renderer';

// Pre-define logo paths for optimization
const LOGO_LEFT = "/edulabslo.png";
const LOGO_RIGHT = "/thgstep.png";

// Register a font for bold if needed
// Font.register({ family: 'Arial', src: 'https://fonts.gstatic.com/s/arial.ttf' });

const PAGE_WIDTH = 841.89; // A4 landscape width
const PAGE_HEIGHT = 595.28; // A4 landscape height
const PAGE_MARGIN = 20;
const GAP = 10;
const TICKETS_PER_ROW = 3;
const TICKETS_PER_COL = 3;
// Calculate exact dimensions for 3x3 grid
const AVAILABLE_WIDTH = PAGE_WIDTH - (PAGE_MARGIN * 2);
const AVAILABLE_HEIGHT = PAGE_HEIGHT - (PAGE_MARGIN * 2);
const TICKET_WIDTH = (AVAILABLE_WIDTH - (GAP * (TICKETS_PER_ROW - 1))) / TICKETS_PER_ROW;
const TICKET_HEIGHT = (AVAILABLE_HEIGHT - (GAP * (TICKETS_PER_COL - 1))) / TICKETS_PER_COL;

const styles = StyleSheet.create({
  page: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    backgroundColor: '#fff',
    padding: PAGE_MARGIN,
    width: PAGE_WIDTH,
    height: PAGE_HEIGHT,
  },
  grid: {
    position: 'relative',
    width: AVAILABLE_WIDTH,
    height: AVAILABLE_HEIGHT,
  },
  ticket: {
    position: 'absolute',
    width: TICKET_WIDTH,
    height: TICKET_HEIGHT,
    border: '1.5px solid #222',
    padding: 10,
    display: 'flex',
    flexDirection: 'column',
    fontSize: 11,
    justifyContent: 'flex-start',
    backgroundColor: '#fff',
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 3,
    paddingHorizontal: 0,
  },
  logoLeft: {
    width: 60,
    height: 50,
    alignItems: 'center',
    justifyContent: 'flex-start',
    marginLeft: -5,
  },
  logoRight: {
    width: 60,
    height: 50,
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginRight: -5,
  },
  logoImage: {
    width: 48,
    height: 48,
    objectFit: 'contain',
  },
  centerContent: {
    flex: 1,
    alignItems: 'center',
    marginHorizontal: 0,
  },
  eventTitle: {
    fontWeight: 'bold',
    fontSize: 12,
    textAlign: 'center',
    marginBottom: 2,
  },
  schoolName: {
    fontSize: 10,
    textAlign: 'center',
    marginBottom: 4,
  },
  studentInfo: {
    marginTop: 8,
    paddingHorizontal: 0,
  },
  row: {
    flexDirection: 'row',
    marginBottom: 4,
    alignItems: 'flex-start',
  },
  label: {
    fontSize: 9,
    fontWeight: 'bold',
    minWidth: 58,
    flexShrink: 0,
  },
  value: {
    fontSize: 9,
    marginLeft: 3,
    fontWeight: 'normal',
    flex: 1,
  },
  footer: {
    fontSize: 9,
    textAlign: 'left',
    marginTop: 6,
  },
  bold: {
    fontWeight: 'bold',
  },
  whatsappRow: {
    flexDirection: 'row',
    marginTop: 4,
    justifyContent: 'center',
    paddingVertical: 2,
    paddingHorizontal: 4,
    marginHorizontal: 2,
  },
  whatsappText: {
    fontSize: 7,
    textAlign: 'center',
    fontWeight: 'bold',
  },
});

interface Student {
  id: string;
  name?: string; // Make optional to match ClassesPage
  fullName?: string; // API field
  grade?: string;
  class?: string;
  section?: string;
  subjects?: string[];
  phoneNumber?: string;
  phone?: string; // Legacy field
  securityPin?: string;
  isEmpty?: boolean; // For empty slots
  [key: string]: any;
}

interface HallTicketPdfProps {
  students: Student[];
  schoolName: string;
  year: string;
}

// Memoized static header component - renders once per school
const StaticHeader = React.memo(({ schoolName }: { schoolName: string }) => {
  // Split school name by comma and render each part on separate lines
  const schoolNameParts = schoolName.split(',').map(part => part.trim());

  return (
    <View style={styles.headerRow}>
      <View style={styles.logoLeft}>
        <Image src={LOGO_LEFT} style={styles.logoImage} />
      </View>
      <View style={styles.centerContent}>
        <Text style={styles.eventTitle}>LiveOlympiad 2025-26</Text>
        {schoolNameParts.map((part, index) => (
          <Text key={index} style={styles.schoolName}>{part}</Text>
        ))}
      </View>
      <View style={styles.logoRight}>
        <Image src={LOGO_RIGHT} style={styles.logoImage} />
      </View>
    </View>
  );
});

// Memoized dynamic student content component
const DynamicStudentContent = React.memo(({ student }: { student: Student }) => (
  <View style={styles.studentInfo}>
    <View style={styles.row}>
      <Text style={styles.label}>Name:</Text>
      <Text style={[styles.value, styles.bold]}>{student.name || student.fullName || ''}</Text>
    </View>
    <View style={styles.row}>
      <Text style={styles.label}>Class:</Text>
      <Text style={[styles.value, { flex: 0, minWidth: 80 }]}>
        {student.grade || student.class || ''}
      </Text>
      {student.section && (
        <>
          <Text style={[styles.label, { minWidth: 60 }]}>      Section:</Text>
          <Text style={styles.value}>{student.section}</Text>
        </>
      )}
    </View>
    <View style={styles.row}>
      <Text style={styles.label}>Subjects:</Text>
      <Text style={styles.value}>
        {student.subjects && student.subjects.length > 0
          ? student.subjects.join(', ')
          : '-'
        }
      </Text>
    </View>
    <View style={styles.row}>
      <Text style={styles.label}>Login ID:</Text>
      <Text style={[styles.value, { flex: 0, minWidth: 80 }]}>
        {(student.phoneNumber || student.phone || '').replace(/^\+91/, '') || '-'}
      </Text>
      <Text style={[styles.label, { minWidth: 60 }]}>      PIN:</Text>
      <Text style={styles.value}>{student.securityPin || '-'}</Text>
    </View>
    <View style={styles.row}>
      <Text style={styles.label}>Web:</Text>
      <Text style={styles.value}>https://app.liveolympiad.org</Text>
    </View>
    {/* WhatsApp Support Footer */}
    <View style={styles.whatsappRow}>
      <Text style={styles.whatsappText}>Whatsapp Support: +91 7011395619</Text>
    </View>
  </View>
));

// Memoized individual ticket component
const TicketComponent = React.memo(({
  student,
  schoolName,
  ticketStyle
}: {
  student: Student;
  schoolName: string;
  ticketStyle: any;
}) => {
  if (student.isEmpty) {
    return <View style={ticketStyle} key={student.id} />;
  }

  return (
    <View style={ticketStyle} key={student.id}>
      <StaticHeader schoolName={schoolName} />
      <DynamicStudentContent student={student} />
    </View>
  );
});

const HallTicketPdf: React.FC<HallTicketPdfProps> = ({ students, schoolName, year }) => {
  // Memoize pages calculation to avoid recalculation on re-renders
  const pages = React.useMemo(() => {
    // Students are already sorted by class (grade) → section → name from the backend
    // No additional sorting needed, just paginate directly for better performance
    const pageArray: Student[][] = [];
    const STUDENTS_PER_PAGE = 9;

    for (let i = 0; i < students.length; i += STUDENTS_PER_PAGE) {
      const pageStudents = students.slice(i, i + STUDENTS_PER_PAGE);

      // Pad last page with empty slots if needed for consistent layout
      while (pageStudents.length < STUDENTS_PER_PAGE && pageStudents.length > 0) {
        pageStudents.push({
          id: `empty-${pageStudents.length}`,
          name: '',
          fullName: '',
          isEmpty: true
        });
      }

      pageArray.push(pageStudents);
    }

    return pageArray;
  }, [students]);

  return (
    <Document>
      {pages.map((pageStudents, pageIndex) => (
        <Page
          size="A4"
          orientation="landscape"
          style={styles.page}
          key={pageIndex}
        >
          <View style={styles.grid}>
            {pageStudents.map((student, idx) => {
              // Calculate absolute position for 3x3 grid
              const row = Math.floor(idx / TICKETS_PER_ROW);
              const col = idx % TICKETS_PER_ROW;
              const left = col * (TICKET_WIDTH + GAP);
              const top = row * (TICKET_HEIGHT + GAP);

              const ticketStyle = {
                ...styles.ticket,
                left: left,
                top: top,
              };

              return (
                <TicketComponent
                  key={student.id}
                  student={student}
                  schoolName={schoolName}
                  ticketStyle={ticketStyle}
                />
              );
            })}
          </View>
        </Page>
      ))}
    </Document>
  );
};

export default HallTicketPdf; 